import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight, Heart } from 'lucide-react';
import { SiInstagram, SiLinkedin, SiX } from 'react-icons/si';
import { Button } from '@/components/ui/button';
import { IMAGES } from '@/assets/images';
import { ROUTE_PATHS, scrollToSection } from '@/lib/index';
import { springPresets } from '@/lib/motion';
interface LayoutProps {
  children: React.ReactNode;
}
export function Layout({
  children
}: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navItems = [{
    name: 'Manifesto',
    id: 'manifesto'
  }, {
    name: 'Share Kindness',
    id: 'share'
  }, {
    name: 'The Movement',
    id: 'movement'
  }];
  const handleNavClick = (id: string) => {
    setIsMenuOpen(false);
    if (location.pathname === ROUTE_PATHS.HOME) {
      scrollToSection(id);
    } else {
      window.location.href = `${ROUTE_PATHS.HOME}#${id}`;
    }
  };
  return <div className="flex flex-col min-h-screen bg-background">
      {/* Navigation Header */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-background/90 backdrop-blur-md py-3 shadow-sm border-b border-border' : 'bg-transparent py-6'}`}>
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3 group" onClick={() => window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })}>
            <img src={IMAGES.KINDSIGHT_LOGO_FOR_WEB} alt="KindSight Logo" className="h-10 w-10 md:h-12 md:w-12 transition-transform group-hover:scale-105" />
            <div className="flex flex-col">
              <span className="text-xl md:text-2xl font-bold text-foreground leading-tight tracking-tight">
                KindSight
              </span>
              <span className="text-[10px] md:text-xs font-medium text-muted-foreground uppercase tracking-[0.2em]">
                Center Kindness in Care
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map(item => <button key={item.id} onClick={() => handleNavClick(item.id)} className="text-sm font-semibold text-foreground/80 hover:text-primary transition-colors cursor-pointer">
                {item.name}
              </button>)}
            <Button onClick={() => handleNavClick('share')} className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg shadow-accent/20 rounded-full px-6">
              Share Your Sighting
            </Button>
          </nav>

          {/* Mobile Toggle */}
          <button className="md:hidden text-foreground" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle Menu">
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={springPresets.gentle} className="fixed inset-0 z-40 bg-background md:hidden pt-24 px-6">
            <div className="flex flex-col gap-6">
              {navItems.map(item => <button key={item.id} onClick={() => handleNavClick(item.id)} className="text-2xl font-bold text-foreground text-left border-b border-border pb-4">
                  {item.name}
                </button>)}
              <Button onClick={() => handleNavClick('share')} className="w-full bg-accent text-accent-foreground py-6 text-lg rounded-xl">
                Share Your Sighting
              </Button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-foreground text-background pt-24 pb-12 overflow-hidden relative">
        {/* Forward Movement Decoration */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent pointer-events-none" />
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <img src={IMAGES.KINDSIGHT_LOGO_FOR_WEB} alt="KindSight Logo" className="h-12 w-12" />
                <span className="text-3xl font-bold tracking-tight">KindSight</span>
              </div>
              <p className="text-background/70 max-w-md text-lg leading-relaxed mb-8">
                Moving healthcare forward by centering the moments of connection, 
                empathy, and kindness that define the true spirit of care.
              </p>
              <div className="flex gap-4">
                
                
                
              </div>
            </div>

            <div>
              <h4 className="font-mono text-xs uppercase tracking-widest text-primary mb-6">The Movement</h4>
              <ul className="space-y-4">
                {navItems.map(item => <li key={item.id}>
                    <button onClick={() => handleNavClick(item.id)} className="text-background/60 hover:text-background transition-colors flex items-center group">
                      {item.name}
                      <ArrowRight size={14} className="ml-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </button>
                  </li>)}
              </ul>
            </div>

            <div>
              <h4 className="font-mono text-xs uppercase tracking-widest text-accent mb-6">Join Us</h4>
              <p className="text-background/60 text-sm mb-6">
                Invite others to share where they have "Sighted Kindness" in a healthcare setting.
              </p>
              <Button variant="outline" className="border-background/20 text-background hover:bg-background hover:text-foreground w-full" onClick={() => handleNavClick('share')}>
                Submit a Sighting
              </Button>
            </div>
          </div>

          <div className="pt-12 border-t border-background/10 flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-background/40 text-sm">
              © 2026 KindSight. All rights reserved. Centering Kindness in Care.
            </p>
            <div className="flex items-center gap-2 text-background/40 text-sm">
              <span>Made with</span>
              <Heart size={14} className="text-accent fill-accent animate-pulse" />
              <span>for Healthcare Communities</span>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}